import React, { useMemo, useState, useEffect, useCallback, useRef } from 'react';
import {
    Box,
    Grid,
    Typography,
    Stack,
    IconButton
} from '@mui/material';
import SimpleDateFilter from '../Dropdown/SimpleDateFilter';
import PageWrapper from 'components/Common/PageWraper';
import KpiCard from '../TableauObs/common/KpiCard';
import {
    fetchProductCatalogOverview,
    fetchUpcomingRenewals,
    fetchExpiredContracts,
    fetchRenewalsTableData,
    fetchRenewalsFilterOptions,
    exportRenewalsData,
    exportRenewalsByOwner,
    fetchBudgetVariance,
    fetchRenewalCost,
    fetchTotalCurrentSpend,
    fetchBillingCycleDistribution
} from '../../service/service';
import { CustomizedDataGrid } from 'components/customized_data_grid';
import LoaderComponent from 'components/Loader';
import RowTypography from 'components/Table/RowTypography';
import { formatValue } from 'components/PowerBIObservability/UserActivity/helper';
import RenewalTimelineWidget, { type RenewalTimelineData } from './RenewalTimelineWidget';
import ProgressChartWidget from 'components/Widgets/ProgressChart';
import BillingCycleDistribution, { BillingCycleItem } from './BillingCycleDistribution';
import LicenseModelDistributionWidget from './LicenseModelDistributionWidget';
import PieChartWithLegendWidget from './PieChartWithLegendWidget';
import { ChevronRight } from '@mui/icons-material';

type LicenseTab = 'critical' | 'owner';

const TABS: { id: LicenseTab; label: string }[] = [
    { id: 'critical', label: 'Critical Renewals' },
    { id: 'owner', label: 'Renewals By Owner' }
];

const TIME_FILTER_OPTIONS_TABLE = [
    { value: '30', label: 'Next 30 Days' },
    { value: '60', label: 'Next 60 Days' },
    { value: '90', label: 'Next 90 Days' },
];

const formatCurrency = (amount: number | null | undefined): string => {
    if (amount === null || amount === undefined || amount === 0) return '-';
    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
    }).format(amount);
};

type FormatterProps = {
    row: any;
};

interface ExpiredContract {
    currentCount: number;
    previousCount: number;
    totalCount: number;
    riskPercentage: number;
    changePercentage: number;
    trend: 'increase' | 'decrease' | 'flat';
    daterange: {
        start: string | null;
        end: string;
        prevStart: string | null;
        prevEnd: string | null;
    };
}

const ExpandFormatter: React.FC<{ 
    expanded: boolean; 
    onCellExpand: () => void;
}> = ({ expanded, onCellExpand }) => {
    return (
        <IconButton
            size="small"
            onClick={onCellExpand}
            sx={{
                padding: '2px',
                marginLeft: '4px',
                transition: 'transform 0.2s ease',
                transform: expanded ? 'rotate(90deg)' : 'rotate(0deg)',
                '&:hover': {
                    backgroundColor: 'rgba(0, 0, 0, 0.04)'
                }
            }}
        >
            <ChevronRight sx={{ fontSize: 20, fontWeight: 700, color:'#003771' }} />
        </IconButton>
    );
};

const OwnerNameFormatter: React.FC<FormatterProps & { onToggle: (row: any) => void }> = ({ row, onToggle }) => {
    const isParent = row.productCount !== undefined && row.products !== undefined;
    const hasProducts = isParent && row.productCount > 0;
    const isUnassigned = row.applicationOwner === 'Unassigned';
    
    if (!isParent) {
        return (
            <Stack direction="row" alignItems="center" spacing={1} sx={{ pl: 6 }}>
                <RowTypography sx={{ fontWeight: 500 }}>
                    {row.productName || '-'}
                </RowTypography>
            </Stack>
        );
    }
    
    return (
        <Stack direction="row" alignItems="center" spacing={1} height="100%">
            {hasProducts && (
                <ExpandFormatter
                    expanded={row.isExpanded === true}
                    onCellExpand={() => onToggle(row)}
                   
                />
            )}
            <Typography
                sx={{
                    fontWeight: 700,
                    color: isUnassigned ? '#D82B51' : '#003771',
                    paddingLeft: !hasProducts ? '32px' : '0px',
                    cursor: hasProducts ? 'pointer' : 'default'
                }}
                onClick={() => {
                    if (hasProducts) onToggle(row);
                }}
            >
                {row.applicationOwner}
            </Typography>
        </Stack>
    );
};

const DaysFormatter: React.FC<FormatterProps> = ({ row }) => {
    const isParent = row.productCount !== undefined && row.products !== undefined;
        if (isParent) {
        return <RowTypography>-</RowTypography>;
    }
    const days = row.remainingDays;
    if (days === undefined || days === null) return <RowTypography>-</RowTypography>; 
    const displayValue = days < 0 ? `Overdue by ${Math.abs(days)} days` : `${days} days`;
    
    return (
        <RowTypography >
            {displayValue}
        </RowTypography>
    );
};

const CostFormatter: React.FC<FormatterProps> = ({ row }) => {
    const isParent = row.productCount !== undefined && row.products !== undefined;
    const cost = isParent ? row.totalCost : row.renewalBudget;
    return (
        <RowTypography sx={{ fontWeight: isParent?700:500,  color: isParent ? '#003771' : '', }}>
            {formatCurrency(cost)}
        </RowTypography>
    );
};

const getIssueStyle = (hasIssue: boolean) => ({
    color: hasIssue ? '#D82B51' : 'inherit',
    fontWeight: hasIssue ? 600 : 400
});

const IssuesFormatter: React.FC<FormatterProps> = ({ row }) => {
    const isParent = row.productCount !== undefined && row.products !== undefined;

    if (isParent) {
        const issuesCount = row.issueCount || 0;
        const hasIssues = issuesCount > 0;
        let label: string;

        if (hasIssues) {
         label = `${issuesCount} issue${issuesCount > 1 ? 's' : ''}`;
        } else {
         label = '-';
        }

        return (
            <RowTypography sx={getIssueStyle(hasIssues)}>
                {label}
            </RowTypography>
        );
    }

    if (!row.issues) {
        return <RowTypography>-</RowTypography>;
    }

    const hasIssue = row.issues !== '-';

    return (
        <RowTypography sx={getIssueStyle(hasIssue)}>
            {row.issues}
        </RowTypography>
    );
};

function OverViewHeader({
    activeTab,
    onTabChange,
    timeRange,
    handleDateChange,
    onFilterChange
}: Readonly<{
    activeTab: LicenseTab;
    onTabChange: (tab: LicenseTab) => void;
    timeRange: string;
    handleDateChange: (value: string) => void;
    onFilterChange?: (days: string) => void;
}>) {
    return (
        <Box display="flex" flexDirection="column" gap={2} width="100%">
            {activeTab === 'critical' && <Box display="flex" justifyContent="space-between" alignItems="center">
                <SimpleDateFilter
                    filterMonth={timeRange}
                    setFilterMonth={() => { }}
                    handleChange={(value) => {
                        handleDateChange(value);
                        onFilterChange?.(value);
                    }}
                    customOptions={TIME_FILTER_OPTIONS_TABLE}
                    renderValue={(val: string) => {
                        const opt = TIME_FILTER_OPTIONS_TABLE.find(o => o.value === val);
                        return opt ? opt.label : '';
                    }}
                    boxProps={{
                        sx: {
                            minWidth: { xs: 'auto', md: '13.75rem' },
                            backgroundColor: '#143968ff',
                            '& .MuiSelect-select': {
                                color: '#ffffff',
                            },
                        },
                    }}
                />
            </Box>}
            <Box display="flex" gap={4}>
                {TABS.map((tab) => {
                    const isActive = tab.id === activeTab;
                    return (
                        <Box
                            key={tab.id}
                            sx={{
                                cursor: 'pointer',
                                pb: 0.5,
                                borderBottom: isActive ? '3px solid #15A7E4' : '3px solid transparent',
                                color: isActive ? '#003771' : '#4A5A6A',
                                transition: 'all 0.2s ease',
                            }}
                            onClick={() => onTabChange(tab.id)}
                        >
                            <Typography
                                variant="subtitle1"
                                sx={{
                                    fontWeight: isActive ? 700 : 500,
                                    fontSize: 16,
                                }}
                            >
                                {tab.label}
                            </Typography>
                        </Box>
                    );
                })}
            </Box>
        </Box>
    );
}

export const DEFAULT_TIME_RANGE = 'ALL_TIME';

export const TIME_FILTER_OPTIONS = [
    { value: 'ALL_TIME', label: 'All Time' },
    { value: 'THIS_YEAR', label: 'This Year' },
    { value: 'LAST_30_DAYS', label: 'Last 30 Days' },
    { value: 'LAST_60_DAYS', label: 'Last 60 Days' },
    { value: 'LAST_90_DAYS', label: 'Last 90 Days' }];

const COLOR_PALETTE = [
    "#4A90E2",
    "#01C008",
    "#FFA500",
    "#E74C3C",
    "#9B59B6",
    "#2eaacc",
    "#E67E22",
    "#16A085",
    "#ad445e",
    "#f1570f",
    "#2980B9",
];

const NOT_APPLICABLE_COLOR = "#a1a1a1ff";
interface TotalCostSpendItem {
    axisName: string;
    cost: number;
}

interface TotalCostSpendsData {
    products: TotalCostSpendItem[];
    vendors: TotalCostSpendItem[];
    publishers: TotalCostSpendItem[];
}

interface ProductCatalogProps {
    filters?: {
        filterOption?: string;
        startDate?: string;
        endDate?: string;
        filterList?: null | { [key: string]: any };
        chartTimePeriod?: string;
    };
    checkedListRef?: React.MutableRefObject<any>;
    resetFiltersRef?: React.MutableRefObject<(() => void) | null>;
}

function getColumns(activeTab: LicenseTab, handleOwnerToggle: (row: any) => void) {
    if (activeTab === 'critical') {
        return [
            { 
                key: "productName", 
                name: "Product Name", 
                minWidth: 200,
                formatter: ({ row }: any) => (
                    <Stack direction="row" alignItems="center" spacing={1}>
                        <Typography variant="body2">{row.productName || '-'}</Typography>
                    </Stack>
                ),
                resizable: true,
                flexGrow: 1,
                sortable: true
            },
            { 
                key: "remainingDays", 
                name: "Remaining Days", 
                minWidth: 160,
                formatter: DaysFormatter,
                resizable: true,
                flexGrow: 1,
                sortable: true
            },
            { 
                key: "renewalBudget", 
                name: "Renewal Budget", 
                minWidth: 160,
                formatter: ({ row }: any) => (
                    <RowTypography sx={{ fontWeight: 500 }}>
                        {formatCurrency(row.renewalBudget)}
                    </RowTypography>
                ),
                resizable: true,
                flexGrow: 1,
                sortable: true
            },
            { 
                key: "renewalNotes", 
                name: "Renewal Notes", 
                minWidth: 200,
                formatter: ({ row }: any) => (
                    <RowTypography>{row.renewalNotes || '-'}</RowTypography>
                ),
                resizable: true,
                flexGrow: 2,
                sortable: true
            },
        ];
    } else {
        return [
            { 
                key: "applicationOwner", 
                name: "Owner with Products", 
                minWidth: 300,
                formatter: ({ row, isCellSelected }: any) => (
                    <OwnerNameFormatter 
                        row={row} 
                        onToggle={handleOwnerToggle}
                       
                    />
                ),
                resizable: true,
                flexGrow: 2,
                sortable: true 
            },
            { 
                key: "remainingDays", 
                name: "Days", 
                minWidth: 120,
                formatter: DaysFormatter,
                resizable: true,
                flexGrow: 1,
                sortable: true 
            },
            { 
                key: "renewalBudget", 
                name: "Cost", 
                minWidth: 140,
                formatter: CostFormatter,
                resizable: true,
                flexGrow: 1,
                sortable: true 
            },
            { 
                key: "issues", 
                name: "Issues", 
                minWidth: 200,
                formatter: IssuesFormatter,
                resizable: true,
                flexGrow: 1.5,
                sortable: true 
            },
        ];
    }
}

const ProductCatalogOverview: React.FC<ProductCatalogProps> = ({ checkedListRef, filters: externalFilters, resetFiltersRef }) => {

    type OverviewType = "missingMetadata" | "productsByPublisher" | "unassignedOwners" | "deploymentRatio" | "billingCycle" | "licensesModel" | "totalCostSpend";
    const [timeRange] = useState(DEFAULT_TIME_RANGE);
    const [activeTab, setActiveTab] = useState<LicenseTab>('critical');
    const [tableData, setTableData] = useState<any[]>([]);
    const [tableLoading, setTableLoading] = useState(false);
    const [tableDaysFilter, setTableDaysFilter] = useState<string>('30'); 
    const [tablePagination, setTablePagination] = useState({
        currentPage: 1,
        totalPages: 0,
        totalElements: 0,
        pageSize: 10
    });
    
    const ownerDataRef = useRef<any[]>([]); 
    const [displayData, setDisplayData] = useState<any[]>([]); 
    const [ownerLoading, setOwnerLoading] = useState(false);
    const [ownerPagination, setOwnerPagination] = useState({
        currentPage: 1,
        totalPages: 0,
        totalElements: 0,
        pageSize: 10
    });
    
    const [sortConfig, setSortConfig] = useState<{
        sortBy: string;
        sortDir: 'ASC' | 'DESC';
    }>({
        sortBy: 'remainingDays',
        sortDir: 'ASC'
    });
    
    const [currentFilters, setCurrentFilters] = useState<Record<string, any>>({});
    const [searchValues, setSearchValues] = useState<Record<string, string>>({});
    const [filterDataOptions, setFilterDataOptions] = useState<Record<string, string[]>>({
        productName: [],
        applicationOwner: [],
        publisher: [],
        renewalStatus: [],
        managedBy: [],
        licenseModel: [],
        cloudOnPrem: []
    });
    const [filterLoading, setFilterLoading] = useState(false);

    const [tableTimeRange, setTableTimeRange] = useState<string>('30');

    type TotalCostSpendsView = 'products' | 'vendors' | 'publishers';
    const [totalCostSpendsView, setTotalCostSpendsView] = useState<TotalCostSpendsView>('products');
    
    const [totalCostSpendsData, setTotalCostSpendsData] = useState<TotalCostSpendsData>({
        products: [],
        vendors: [],
        publishers: []
    });

    const [licenseModelView, setLicenseModelView] = useState<'cost' | 'count'>('cost');

    const handleDateChangee = (value: string) => {
        setTableTimeRange(value);
    };

    const overviewExportColumns = [
        'Product Name',
        'Remaining Days',
        'Renewal Budget',
        'Renewal Notes'
    ];

    const ownerExportColumns = [
        'Owner with Products',
        'Days',
        'Cost',
        'Issues'
    ];

    const handleExport = useCallback(async () => {
        try {
            if (activeTab === 'critical') {
                let renewalCategory = 'all';
                
                if (tableDaysFilter === '30') {
                    renewalCategory = 'upcoming30';
                } else if (tableDaysFilter === '60') {
                    renewalCategory = 'upcoming60';
                } else if (tableDaysFilter === '90') {
                    renewalCategory = 'upcoming90';
                }

                const exportData = {
                    formatType: "EXCEL",
                    renewalCategory,
                    filters: currentFilters,
                    search: searchValues,
                    sortBy: sortConfig.sortBy,
                    orderBy: sortConfig.sortDir,
                    exportColumns: overviewExportColumns
                };
                await exportRenewalsData(exportData);
            } else {
                 const exportData = {
                    formatType: "EXCEL",
                    filters: currentFilters,
                    search: searchValues,
                    sortBy: sortConfig.sortBy,
                    orderBy: sortConfig.sortDir,
                    exportColumns: ownerExportColumns
                };
                await exportRenewalsByOwner(exportData);
                console.log('Export owner view data:', displayData);
            }
        } catch (error) {
            console.error('Error exporting overview data:', error);
        }
    }, [activeTab, tableDaysFilter, currentFilters, searchValues, sortConfig, overviewExportColumns, displayData]);

    const defaultColumnNames = [
        'Product Name',
        'Remaining Days',
        'Renewal Budget',
        'Renewal Notes',
    ];

    const ownerDefaultColumnNames = [
        'Owner with Products',
        'Days',
        'Cost',
        'Issues'
    ];

    const fetchFilterOptions = useCallback(async () => {
        setFilterLoading(true);
        try {
            const fields = ['productName', 'applicationOwner', 'publisher', 'renewalStatus', 'managedBy', 'licenseModel', 'cloudOnPrem'];
            
            const optionsToPromises = fields.map(field => 
                fetchRenewalsFilterOptions(field)
                    .then(data => ({ field, data }))
                    .catch(error => {
                        console.error(`Error fetching ${field} options:`, error);
                        return { field, data: [] };
                    })
            );

            const results = await Promise.all(optionsToPromises);
            
            const optionsToMap: Record<string, string[]> = {};
            results.forEach(result => {
                optionsToMap[result.field] = result.data;
            });

            setFilterDataOptions(optionsToMap);
        } catch (error) {
            console.error('Error fetching Overview filter options:', error);
        } finally {
            setFilterLoading(false);
        }
    }, []);

    const fetchRenewalsTableDataForOverview = useCallback(async (
        daysFilter: string, 
        page = 1, 
        pageSize = 10,
        sortBy = sortConfig.sortBy,
        sortDir = sortConfig.sortDir
    ) => {
        setTableLoading(true);
        try {
            let renewalCategory = 'all';

            if (daysFilter === '30') {
                renewalCategory = 'upcoming30';
            } else if (daysFilter === '60') {
                renewalCategory = 'upcoming60';
            } else if (daysFilter === '90') {
                renewalCategory = 'upcoming90';
            }

            const payload = {
                page,
                pageSize,
                sortBy: sortBy,
                orderBy: sortDir,
                renewalCategory,
                filters: currentFilters,
                search: searchValues
            };

            const response = await fetchRenewalsTableData(payload);            
            if (response?.renewals?.length > 0) {
                setTableData(response.renewals);
                setTablePagination(response.pagination || {
                    currentPage: page,
                    totalPages: 0,
                    totalElements: 0,
                    pageSize: pageSize
                });
            } else {
                setTableData([]);
                setTablePagination({
                    currentPage: page,
                    totalPages: 0,
                    totalElements: 0,
                    pageSize: pageSize
                });
            }
        } catch (error) {
            console.error('Failed to fetch table data:', error);
            setTableData([]);
        } finally {
            setTableLoading(false);
        }
    }, [currentFilters, searchValues, sortConfig.sortBy, sortConfig.sortDir]);

    const fetchRenewalsByOwnerData = useCallback(async (
    page = 1,
    pageSize = 10
    ) => {
    setOwnerLoading(true);
    try {
        const payload = {
            type: 'renewalsByOwner',
            page,
            pageSize,
            filters: currentFilters,
            search: searchValues,
            sortBy: sortConfig.sortBy,
            sortDir: sortConfig.sortDir
        };

        const response = await fetchProductCatalogOverview(payload);
        
        if (response?.success && response?.data) {
            const owners = response?.data?.data?.owners || [];
            ownerDataRef.current = owners;
            setDisplayData(owners);
            setOwnerPagination({
                currentPage: response.data.data.pagination?.currentPage || page,
                totalPages: response.data.data.pagination?.totalPages || 0,
                totalElements: response.data.data.pagination?.totalElements || 0,
                pageSize: response.data.data.pagination?.pageSize || pageSize
            });
        } else {
            ownerDataRef.current = [];
            setDisplayData([]);
            setOwnerPagination({
                currentPage: page,
                totalPages: 0,
                totalElements: 0,
                pageSize: pageSize
            });
        }
    } catch (error) {
        console.error('Failed to fetch renewals by owner:', error);
        ownerDataRef.current = [];
        setDisplayData([]);
    } finally {
        setOwnerLoading(false);
    }
}, [currentFilters, searchValues, sortConfig.sortBy, sortConfig.sortDir]);

    const handleOwnerToggle = useCallback((row: any) => {
        const rows = displayData;
        const rowIndex = rows.findIndex((r: any) => r.applicationOwner === row.applicationOwner);
        
        if (rowIndex === -1) return;

        const currentRow = rows[rowIndex];
        const newRows = [...rows];

        if (!currentRow.isExpanded) {
            const children = currentRow.products.map((product: any) => ({
                ...product,
                isChildren: true,
                isProduct: true,
                parentId: currentRow.applicationOwner
            }));
                children.sort((a: any, b: any) => {
                const daysA = a.remainingDays ?? 9999;
                const daysB = b.remainingDays ?? 9999;
                return daysA - daysB;
            });
            
            newRows.splice(rowIndex + 1, 0, ...children);
            newRows[rowIndex] = { ...currentRow, isExpanded: true };
        } else {
            const children = currentRow.products;
            newRows.splice(rowIndex + 1, children?.length || 0);
            newRows[rowIndex] = { ...currentRow, isExpanded: false };
        }
        
        setDisplayData(newRows);
    }, [displayData]);

    const handleOwnerSorting = useCallback((sorting: any) => {
    setSortConfig({
        sortBy: sorting.sortBy,
        sortDir: sorting.sortDir
    });
    setOwnerPagination(prev => ({ ...prev, currentPage: 1 }));
}, []);

    const handleOwnerPageChange = useCallback((event: any, value: any) => {
        setOwnerPagination(prev => ({ ...prev, currentPage: value }));
        fetchRenewalsByOwnerData(value, ownerPagination.pageSize);
    }, [fetchRenewalsByOwnerData, ownerPagination.pageSize]);

    const handleOwnerPageSizeChange = useCallback((event: any) => {
        const newSize = Number.parseInt(event.target.value);
        setOwnerPagination(prev => ({ ...prev, pageSize: newSize, currentPage: 1 }));
        fetchRenewalsByOwnerData(1, newSize);
    }, [fetchRenewalsByOwnerData]);

    const handleOwnerFilterChange = useCallback((filter: { [key: string]: any }) => {
        const filterListMap: { [key: string]: any } = {};
        Object.keys(filter).forEach((key) => {
            const value = filter[key];
            if (value != null && (!Array.isArray(value) || value.length > 0)) {
                filterListMap[key] = value;
            }
        });
        setCurrentFilters(filterListMap);
        setOwnerPagination(prev => ({ ...prev, currentPage: 1 }));
        
        setTimeout(() => {
            fetchRenewalsByOwnerData(1, ownerPagination.pageSize);
        }, 500);
    }, [fetchRenewalsByOwnerData, ownerPagination.pageSize]);

    const handleOwnerSearch = useCallback((searchDetails: any, columnKey: any) => {
        setSearchValues(prev => ({
            ...prev,
            [columnKey]: searchDetails
        }));
        setOwnerPagination(prev => ({ ...prev, currentPage: 1 }));
        setTimeout(() => {
            fetchRenewalsByOwnerData(1, ownerPagination.pageSize);
        }, 500);
    }, [fetchRenewalsByOwnerData, ownerPagination.pageSize]);

    const handleTableFilterChange = useCallback((days: string) => {
        setTableDaysFilter(days);
        setTablePagination(prev => ({ ...prev, currentPage: 1 }));
        if (activeTab === 'critical') {
            fetchRenewalsTableDataForOverview(days, 1, tablePagination.pageSize, sortConfig.sortBy, sortConfig.sortDir);
        }
    }, [activeTab, fetchRenewalsTableDataForOverview, tablePagination.pageSize, sortConfig]);

    const handleFilterChange = useCallback((filter: { [key: string]: any }) => {
        const filterListMap: { [key: string]: any } = {};
        Object.keys(filter).forEach((key) => {
            const value = filter[key];
            if (value != null && (!Array.isArray(value) || value.length > 0)) {
                filterListMap[key] = value;
            }
        });
        setCurrentFilters(filterListMap);
        setTablePagination(prev => ({ ...prev, currentPage: 1 }));
        
        if (activeTab === 'critical') {
            setTableDaysFilter(prevDaysFilter => {
                fetchRenewalsTableDataForOverview(prevDaysFilter, 1, tablePagination.pageSize, sortConfig.sortBy, sortConfig.sortDir);
                return prevDaysFilter;
            });
        }
    }, [activeTab, fetchRenewalsTableDataForOverview, tablePagination.pageSize, sortConfig]);

    const handleSearch = useCallback((searchDetails: any, columnKey: any) => {
        setSearchValues(prev => ({
            ...prev,
            [columnKey]: searchDetails
        }));
        setTablePagination(prev => ({ ...prev, currentPage: 1 }));
        
        if (activeTab === 'critical') {
            setTableDaysFilter(prevDaysFilter => {
                fetchRenewalsTableDataForOverview(prevDaysFilter, 1, tablePagination.pageSize, sortConfig.sortBy, sortConfig.sortDir);
                return prevDaysFilter;
            });
        }
    }, [activeTab, fetchRenewalsTableDataForOverview, tablePagination.pageSize, sortConfig]);

    const handleSorting = useCallback((sorting: any) => {
        setSortConfig({
            sortBy: sorting.sortBy,
            sortDir: sorting.sortDir
        });        
        setTablePagination(prev => ({ ...prev, currentPage: 1 }));
        
        if (activeTab === 'critical') {
            setTableDaysFilter(prevDaysFilter => {
                fetchRenewalsTableDataForOverview(
                    prevDaysFilter, 
                    1, 
                    tablePagination.pageSize, 
                    sorting.sortBy, 
                    sorting.sortDir
                );
                return prevDaysFilter;
            });
        }
    }, [activeTab, fetchRenewalsTableDataForOverview, tablePagination.pageSize]);

    const handleTablePageChange = useCallback((event: any, value: any) => {
        setTablePagination(prev => ({ ...prev, currentPage: value }));
        setTableDaysFilter(prevDaysFilter => {
            fetchRenewalsTableDataForOverview(
                prevDaysFilter, 
                value, 
                tablePagination.pageSize, 
                sortConfig.sortBy, 
                sortConfig.sortDir
            );
            return prevDaysFilter;
        });
    }, [fetchRenewalsTableDataForOverview, tablePagination.pageSize, sortConfig]);

    const handleTablePageSizeChange = useCallback((event: any) => {
        const newSize = Number.parseInt(event.target.value);
        setTablePagination(prev => ({ ...prev, pageSize: newSize, currentPage: 1 }));
        setTableDaysFilter(prevDaysFilter => {
            fetchRenewalsTableDataForOverview(
                prevDaysFilter, 
                1, 
                newSize, 
                sortConfig.sortBy, 
                sortConfig.sortDir
            );
            return prevDaysFilter;
        });
    }, [fetchRenewalsTableDataForOverview, sortConfig]);

    const handleTabChange = useCallback((tab: LicenseTab) => {
        setActiveTab(tab);
        if (tab === 'critical') {
            setTablePagination(prev => ({ ...prev, currentPage: 1 }));
            fetchRenewalsTableDataForOverview(tableTimeRange, 1, tablePagination.pageSize, sortConfig.sortBy, sortConfig.sortDir);
        } else {
            setOwnerPagination(prev => ({ ...prev, currentPage: 1 }));
            fetchRenewalsByOwnerData(1, ownerPagination.pageSize);
        }
    }, [tableTimeRange, tablePagination.pageSize, ownerPagination.pageSize, sortConfig, fetchRenewalsTableDataForOverview, fetchRenewalsByOwnerData]);

    const headerTitle = (
        <OverViewHeader
            activeTab={activeTab}
            onTabChange={handleTabChange}
            timeRange={tableTimeRange}
            handleDateChange={handleDateChangee}
            onFilterChange={handleTableFilterChange}
        />
    );

    const [loadingState, setLoadingState] = useState({
        missingMetadata: true,
        productsByPublisher: true,
        upcomingRenewals: true,
        expiredContracts: true,
        deploymentRatio: true,
        unassignedOwners: true,
        billingCycle: true,
        licensesModel: true,
        budgetVariance: true,
        renewalCost: true,
        totalCostSpend: true,
        totalCurrentSpend: true,
        renewalsByOwner: true
    });

    const [licensesModelData, setLicensesModelData] = useState<
        Array<{ name: string; value: number }>
        >([]);

    // Generate dynamic color mappings based on data
    const licensesModelColorMapping = useMemo(() => {
        const mapping: { [key: string]: string } = {};
        let colorIndex = 0;

        licensesModelData.forEach((item) => {
            if (item.name === "Not Applicable") {
                mapping[item.name] = NOT_APPLICABLE_COLOR;
            } else {
                mapping[item.name] = COLOR_PALETTE[colorIndex % COLOR_PALETTE.length];
                colorIndex++;
            }
        });

        return mapping;
    }, [licensesModelData]);

    type PieChartState = {
            number: number;
            pieData: Array<{ name: string; value: number }>;
    };

    const createPieChartState = (
        entries: Array<{ name: string; value?: number }>
        ): PieChartState => ({
        number: 0,
        pieData: entries.map(e => ({ name: e.name, value: e.value ?? 0 }))
    });

    const [deploymentRatio, setDeploymentRatio] = useState<PieChartState>(
        createPieChartState([{ name: 'Cloud IaaS' },
             { name: 'Cloud SaaS' }, { name: 'Cloud PaaS' }]
        )
    );

    const getColorMapping = (pieData: Array<{ name: string; value?: number; count?: number }>) : { [key: string]: string } => {
        const mapping: { [key: string]: string } = {};
        let colorIndex = 0;

         pieData.forEach(({ name }: { name: string }) => {
            if (name === "Untagged") {
                mapping[name] = NOT_APPLICABLE_COLOR;
            } else {
                mapping[name] = COLOR_PALETTE[colorIndex % COLOR_PALETTE.length];
                colorIndex++;
            }
        });

        return mapping;
    }


    const setLoading = (key: keyof typeof loadingState, value: boolean) => {
        setLoadingState(prev => ({
            ...prev,
            [key]: value,
        }));
    };

    const [upcomingRenewalsData, setUpcomingRenewalsData] = useState({
        number: 0,
        change: 0,
        changeType: 'increase' as const,
        pieData: [] as Array<{ name: string; value: number }>,
        colorMapping: {
            '30 Days': '#4A90E2',
            '60 Days': '#01C008',
            '90 Days': '#FFA500',
            'Not Applicable': '#808080'
        }
    });

    const [expiredContractsData, setExpiredContractsData] = useState<ExpiredContract>({
    currentCount: 0,
    previousCount: 0,
    totalCount: 0,
    riskPercentage: 0,
    changePercentage: 0,
    trend: 'flat',
    daterange: {
        start: null,
        end: '',
        prevStart: null,
        prevEnd: null
    }
    });
    const [upcomingRenewals30Days, setUpcomingRenewals30Days] = useState<number | null>(null);
    const [delayedRenewals, setDelayedRenewals] = useState<number | null>(null);

    const [budgetVarianceData, setBudgetVarianceData] = useState<any>(null);
    const [renewalCost, setRenewalCost] = useState<number | null>(null);
    const [totalCurrentSpend, setTotalCurrentSpend] = useState<number | null>(null);
    const [billingCycleDistData, setBillingCycleDistData] = useState<BillingCycleItem[]>([]);

    const formatBudgetVariance = (value: number | undefined | null): string | number => {
        if (value == null) return 0;
        return value > 0 ? `+${value}` : value;
    };

    const budgetDifference = budgetVarianceData
        ? (budgetVarianceData.totalOverallCost ?? 0) - (budgetVarianceData.totalRenewalBudget ?? 0)
        : null;

    let budgetDifferenceLabelText: string | undefined = undefined;
    let budgetDifferenceLabelColor = '#D82B51';

    if (budgetDifference !== null) {
        if (budgetDifference < 0) {
            budgetDifferenceLabelText = `$${formatValue(Math.abs(budgetDifference))} Under Budget`;
            budgetDifferenceLabelColor = '#01C008';
        } else if (budgetDifference === 0) {
            budgetDifferenceLabelText = 'On Budget';
            budgetDifferenceLabelColor = '#01C008';
        } else {
            budgetDifferenceLabelText = `$${formatValue(budgetDifference)} Over Budget`;
        }
    }

    const fetchExpiredContractsData = useCallback(async () => {
    setLoading('expiredContracts', true);
    try {
        const response = await fetchExpiredContracts({ timerange: timeRange });

        if (response?.success && response?.data) {
            setExpiredContractsData(response.data);
        } else {
            setExpiredContractsData({
                currentCount: 0,
                previousCount: 0,
                totalCount: 0,
                riskPercentage: 0,
                changePercentage: 0,
                trend: 'flat',
                daterange: {
                    start: null,
                    end: '',
                    prevStart: null,
                    prevEnd: null
                }
            });
        }
    } catch (error) {
        console.error('Error fetching expired contracts:', error);
        setExpiredContractsData({
            currentCount: 0,
            previousCount: 0,
            totalCount: 0,
            riskPercentage: 0,
            changePercentage: 0,
            trend: 'flat',
            daterange: {
                start: null,
                end: '',
                prevStart: null,
                prevEnd: null
            }
        });
    } finally {
        setLoading('expiredContracts', false);
    }
    }, [timeRange]);

    const fetchOverviewSummary = useCallback(async (type: OverviewType) => {
        setLoading(type, true);
        try {
            const payload = { type, ...(timeRange && { filterOption: timeRange }) };
            const response = await fetchProductCatalogOverview(payload);

            if (response?.success && response?.data) {
                const data = response.data;
                if (type === "deploymentRatio") {
                    setDeploymentRatio({
                        number: data.number || 0,
                        pieData: data.pieData || []
                    });
                } else if (type === "licensesModel") {
                    setLicensesModelData(data || []);
                } else if (type === "totalCostSpend") {
                    setTotalCostSpendsData({
                        products: data?.data?.products || [],
                        vendors: data?.data?.vendors || [],
                        publishers: data?.data?.publishers || []
                    });
                }
            }
        } catch (error) {
            console.error(`Error fetching Overview: Type:${type}`, error);
        } finally {
            setLoading(type, false);
        }
    }, [timeRange]);

    const fetchUpcomingRenewalsData = useCallback(async () => {
        setLoading('upcomingRenewals', true);
        try {
            const response = await fetchUpcomingRenewals();

            if (response?.success && response?.data) {
                const data = response.data;
                const upcomingRenewals = data.upcomingRenewals;
                setUpcomingRenewals30Days(upcomingRenewals['30_days']?.count ?? 0);
                setDelayedRenewals(upcomingRenewals['delayedRenewals']?.count ?? 0);

                const pieData = [
                    { name: '30 Days', value: upcomingRenewals['30_days']?.count || 0 },
                    { name: '60 Days', value: upcomingRenewals['60_days']?.count || 0 },
                    { name: '90 Days', value: upcomingRenewals['90_days']?.count || 0 },
                    { name: 'Not Applicable', value: upcomingRenewals['notApplicable']?.count || 0 }
                ];

                setUpcomingRenewalsData({
                    number: data.total || 0,
                    change: 0,
                    changeType: 'increase',
                    pieData: pieData,
                    colorMapping: {
                        '30 Days': '#4A90E2',
                        '60 Days': '#01C008',
                        '90 Days': '#FFA500',
                        'Not Applicable': '#808080'
                    }
                });
            }
        } catch (error) {
            console.error('Error fetching upcoming renewals:', error);
        } finally {
            setLoading('upcomingRenewals', false);
        }
    }, []);

    const renewalTimelineData: RenewalTimelineData = useMemo(() => {
        const pie = upcomingRenewalsData.pieData;
        const get = (name: string) => pie.find((p) => p.name === name)?.value ?? 0;
        return {
            days0To30: get('30 Days'),
            days31To60: get('60 Days'),
            days61To90: get('90 Days'),
        };
    }, [upcomingRenewalsData.pieData]);

    const fetchBudgetVarianceData = useCallback(async () => {
        setLoading('budgetVariance', true);
        try {
        const response = await fetchBudgetVariance();

        if (response?.success && response?.data) {
        setBudgetVarianceData(response.data);
        }
        } catch (error) {
        console.error('Error fetching budget variance:', error);
        } finally {
        setLoading('budgetVariance', false);
        }
    }, []);

    const fetchRenewalCostData = useCallback(async () => {
        setLoading('renewalCost', true);
        try {
            const response = await fetchRenewalCost();
            if (response?.success && response?.data) {
                setRenewalCost(response.data.totalRenewalCost ?? 0);
            }
        } catch (error) {
            console.error('Error fetching renewal cost:', error);
        } finally {
            setLoading('renewalCost', false);
        }
    }, []);
    const totalCostSpendsChartData = useMemo(() => {
        const list = totalCostSpendsData[totalCostSpendsView] || [];
        const sortedList = [...list].sort((a, b) => b.cost - a.cost);
        const displayList = sortedList;       
        const envNames = displayList.map((d) => d.axisName);
        const costsReversed = [...displayList].reverse().map((d) => d.cost);
        const maxCost = Math.max(...displayList.map(d => d.cost), 0);
        const axisMax = Math.ceil(maxCost * 1.1); 
        const seriesData = [
            {
                name: 'Cost',
                type: 'bar',
                data: costsReversed,
                itemStyle: {
                    color: '#00B4C5',
                    borderRadius: [0, 8, 8, 0],
                },
                showBackground: false,
                backgroundStyle: { color: 'rgba(74, 144, 226, 0.15)' },
                label: { show: false },
                barCategoryGap: '40%',
            },
        ];
        
        return {
            data: displayList,
            envNames,
            seriesData,
            axisMax,
            totalItems: sortedList.length
        };
    }, [totalCostSpendsData, totalCostSpendsView]);

    const modifiedSeriesData = totalCostSpendsChartData.seriesData.map((series: any) => ({
        ...series,
        barCategoryGap: '40%',
    }));

    const fetchTotalCurrentSpendData = useCallback(async () => {
        setLoading('totalCurrentSpend', true);
        try {
            const response = await fetchTotalCurrentSpend();
            if (response?.success && response?.data) {
                setTotalCurrentSpend(response.data.totalCurrentSpend ?? 0);
            }
        } catch (error) {
            console.error('Error fetching renewal cost:', error);
        } finally {
            setLoading('totalCurrentSpend', false);
        }
    }, [])

    const fetchBillingCycleDistributionData = useCallback(async () => {
        setLoading('billingCycle', true);
        try {
            const response = await fetchBillingCycleDistribution();
            if (response?.success && Array.isArray(response.data)) {
                setBillingCycleDistData(response.data);
            }
        } catch (error) {
            console.error('Error fetching Billing Cycle Distribution:', error);
        } finally {
            setLoading('billingCycle', false);
        }
    }, []);

    useEffect(() => {
        fetchOverviewSummary("productsByPublisher");
        fetchOverviewSummary("deploymentRatio");
        fetchOverviewSummary("licensesModel");
        fetchOverviewSummary("totalCostSpend");
        fetchUpcomingRenewalsData();
        fetchExpiredContractsData();
        fetchBudgetVarianceData();
        fetchRenewalCostData();
        fetchTotalCurrentSpendData();
        fetchBillingCycleDistributionData();
    }, [fetchOverviewSummary, fetchUpcomingRenewalsData, fetchExpiredContractsData,fetchBudgetVarianceData, fetchRenewalCostData, fetchTotalCurrentSpendData, fetchBillingCycleDistributionData]);

    useEffect(()=>{
     fetchOverviewSummary("missingMetadata");
     fetchOverviewSummary("unassignedOwners");
    },[])

    useEffect(()=>{
        fetchFilterOptions(); 
        fetchRenewalsTableDataForOverview(tableTimeRange, 1, 10, sortConfig.sortBy, sortConfig.sortDir); 
    },[fetchRenewalsTableDataForOverview, fetchFilterOptions, tableTimeRange, sortConfig.sortBy, sortConfig.sortDir])

    useEffect(() => {
        if (activeTab === 'owner') {
            fetchRenewalsByOwnerData(ownerPagination.currentPage, ownerPagination.pageSize);
        }
    }, [activeTab, fetchRenewalsByOwnerData]);

    useEffect(() => {
        fetchExpiredContractsData();
    }, [timeRange, fetchExpiredContractsData]);

    useEffect(() => {
    if (activeTab === 'owner' && ownerPagination.currentPage && ownerPagination.pageSize) {
        fetchRenewalsByOwnerData(ownerPagination.currentPage, ownerPagination.pageSize);
    }
}, [sortConfig.sortBy, sortConfig.sortDir, activeTab, fetchRenewalsByOwnerData, ownerPagination.currentPage, ownerPagination.pageSize]);
    
    const hasTotalCostSpendsData = totalCostSpendsChartData.data.length > 0;

    let renderOverviewContent;

  if (activeTab === 'critical') {
  if (tableLoading) {
    renderOverviewContent = <LoaderComponent pt={16} />;
  } else {
    renderOverviewContent = (
      <CustomizedDataGrid
        title={headerTitle}
        tableColumns={getColumns(activeTab, handleOwnerToggle)}
        tableData={tableData}
        tableType="renewals-overview"
        defaultColumnNames={defaultColumnNames}
        onSorting={handleSorting}
        currSorting={{
          sortBy: sortConfig.sortBy,
          sortDir: sortConfig.sortDir
        }}
        filterDataOptions={filterDataOptions}
        searchDataValues={searchValues}
        onFilterCheck={handleFilterChange}
        currFilter={currentFilters}
        onFilterSearch={handleSearch}
        onHeaderSelectAll={() => {}}
        onFilterClick={() => {}}
        showExport={true}
        fileName={`renewals-overview-${tableDaysFilter}-days-report`}
        tableName="renewals-overview"
        formatType="EXCEL"
        exportColumns={overviewExportColumns}
        exportTableData={handleExport}
        isDateRangeFilter={true}
        isDisabled={false}
        isRenderPagination={true}
        isShowMoreButton={true}
        page={tablePagination.currentPage}
        pageSize={tablePagination.pageSize}
        paginationData={tablePagination}
        listLength={tableData.length}
        handlepageSizeChange={handleTablePageSizeChange}
        handlePageChange={handleTablePageChange}
        pageSizeOptions={[100, 200, 500, 1000]}
      />
    );
    }
    } else {
    renderOverviewContent = (
    <CustomizedDataGrid
      title={headerTitle}
      tableColumns={getColumns(activeTab, handleOwnerToggle)}
      tableData={displayData}
      tableType="renewals-by-owner"
      defaultColumnNames={ownerDefaultColumnNames}
      onSorting={handleOwnerSorting}
      currSorting={{
        sortBy: sortConfig.sortBy,
        sortDir: sortConfig.sortDir
      }}
      filterDataOptions={{
        productName: filterDataOptions.productName || [],
        applicationOwner: filterDataOptions.applicationOwner || [],
        publisher: filterDataOptions.publisher || [],
        renewalStatus: filterDataOptions.renewalStatus || [],
        managedBy: filterDataOptions.managedBy || [],
        licenseModel: filterDataOptions.licenseModel || [],
        cloudOnPrem: filterDataOptions.cloudOnPrem || []
      }}
      searchDataValues={searchValues}
      onFilterCheck={handleOwnerFilterChange}
      currFilter={currentFilters}
      onFilterSearch={handleOwnerSearch}
      onHeaderSelectAll={() => {}}
      onFilterClick={() => {}}
      showExport={true}
      fileName={`renewals-by-owner-report-${new Date().toISOString().split('T')[0]}`}
      tableName="renewals-by-owner"
      formatType="EXCEL"
      exportColumns={ownerExportColumns}
      exportTableData={handleExport}
      isDateRangeFilter={true}
      isDisabled={false}
      isRenderPagination={true}
      isShowMoreButton={true}
      page={ownerPagination.currentPage}
      pageSize={ownerPagination.pageSize}
      paginationData={{
        currentPage: ownerPagination.currentPage,
        totalPages: ownerPagination.totalPages,
        totalElements: ownerPagination.totalElements,
        pageSize: ownerPagination.pageSize
      }}
      listLength={ownerPagination.totalElements}
      handlepageSizeChange={handleOwnerPageSizeChange}
      handlePageChange={handleOwnerPageChange}
      pageSizeOptions={[100, 200, 500, 1000]}
      isFetching={ownerLoading || filterLoading}
      childRowClass={true}
      isLighterTheme={true}
      isFinopsProgramsList={true}
    />
    );
    }

    return (
        <PageWrapper sx={{paddingTop: '48px !important'}}>
            <Grid container spacing={3} sx={{ mb: 2 }}>
                <Grid
                    item
                    xs={12}
                    sx={{
                    display: "grid",
                    gap: 2,
                    gridTemplateColumns: {
                        xs: "repeat(1, 1fr)",
                        sm: "repeat(3, 1fr)",   
                        md: "repeat(5, 1fr)",   
                        lg: "repeat(6, 1fr)",   
                    },
                    }}
                >
                    <KpiCard
                        title="Total Current Spend"
                        isCost={true}
                        number={formatValue(totalCurrentSpend ?? 0)}
                        isLoading={loadingState.totalCurrentSpend || totalCurrentSpend === null}
                        hasTooltip
                        tooltipLabel="Total annualized cost of all active products, calculated as the sum of Overall Cost across the catalog"
                        hasDropdown={false}
                        theme="light"
                        containerProps={{
                        sx: { minHeight: "180px" },
                        }}
                    />
                    <KpiCard
                        title="Upcoming Renewals"
                        number={upcomingRenewals30Days ?? 0}
                        isLoading={loadingState.upcomingRenewals || upcomingRenewals30Days === null}
                        hasTooltip
                        tooltipLabel="Number of active products with renewal due within the next 30 days, excluding completed renewals"
                        hasDropdown={false}
                        theme="light"
                        containerProps={{
                        sx: { minHeight: "180px" },
                        }}
                    />
                    <KpiCard
                        title="Delayed Renewals"
                        number={delayedRenewals ?? 0}
                        isLoading={loadingState.upcomingRenewals || delayedRenewals === null}
                        hasTooltip
                        tooltipLabel="Count of renewals where the contract has passed its renewal date and remains incomplete"
                        hasDropdown={false}
                        theme="light"
                        containerProps={{
                        sx: { minHeight: "180px" },
                        }}
                    />
                    <KpiCard
                        title="Renewal Cost (90 Days)"
                        isCost={true}
                        number={formatValue(renewalCost ?? 0)}
                        isLoading={loadingState.renewalCost || renewalCost === null}
                        hasTooltip
                        tooltipLabel="Total renewal budget for contracts expiring within the next 90 days"
                        hasDropdown={false}
                        theme="light"
                        containerProps={{
                            sx: { minHeight: "180px" },
                        }}
                    />
                    <KpiCard
                        title="Budget Variance"
                        number={formatBudgetVariance(budgetVarianceData?.budgetVariance)}
                        isLoading={loadingState.budgetVariance || budgetVarianceData === null}
                        isPercentage={true}
                        hasTooltip
                        tooltipLabel="Percentage difference between total overall spend and planned renewal budget. Positive value indicates over-budget exposure"
                        hasDropdown={false}
                        theme="light"
                        isCustomLabel={true}
                        customLabelText={budgetDifferenceLabelText}
                        customLabelProps={{
                            sx: { 
                                color: budgetDifferenceLabelColor, 
                                fontWeight: '600' 
                            }
                        }}
                        containerProps={{
                            sx: { minHeight: "180px" },
                        }}
                    />
                      <KpiCard
                        title="Expired Contracts"
                        number={expiredContractsData?.currentCount}
                        isLoading={loadingState.expiredContracts || expiredContractsData === null}
                        hasTooltip
                        tooltipLabel="Number of contracts where the renewal date has passed and the agreement is not marked completed."
                        hasDropdown={false}
                        theme="light"
                        containerProps={{
                            sx: { minHeight: "180px" },
                        }}
                    />
                </Grid>
            </Grid>
            <Grid container spacing={2} sx={{ mb: 2 }}>

                 <Grid item xs={12} md={6}>
                    <RenewalTimelineWidget
                        title="Renewal Timeline"
                        data={renewalTimelineData}
                        isLoading={loadingState.upcomingRenewals}
                        hasTooltip={true}
                        tooltipLabel="Distribution of upcoming renewals grouped into 0–30, 31–60, and 61–90 day buckets based on remaining days to renewal."
                        containerProps={{ sx: { minHeight: '280px' } }}
                    />
                </Grid>

                <Grid item xs={12} md={6}>
                    <BillingCycleDistribution
                        data={billingCycleDistData}
                        isLoading={loadingState.billingCycle}
                        hasTooltip={true}
                        tooltipText="Cost distribution of active products grouped by billing frequency (Yearly, Half-Yearly, Quarterly, Monthly and unspecified)"
                    />
                </Grid>

                <Grid item xs={12} md={6}>
                    <LicenseModelDistributionWidget
                        title='License Model Distribution'
                        data={licensesModelData}
                        colorMapping={licensesModelColorMapping}
                        centerLabel='Total'
                        isLoading={loadingState.licensesModel}
                        hasTooltip={true}
                        hasToggle={true}
                        viewType={licenseModelView}
                        onViewTypeChange={(view) => setLicenseModelView(view)}
                        tooltipText={`${licenseModelView == 'cost' ? 'Breakdown of products by license type (Subscription, Term, Multi-Year), shown by total cost' : 'Breakdown of products by license type (Subscription, Term, Multi-Year), shown by count'}`}
                        containerProps={{ sx: { minHeight: '318px' } }}
                    />
                </Grid>
              
                <Grid item xs={12} md={6}>
                    {hasTotalCostSpendsData ? (
                        <ProgressChartWidget
                            title={"Total Cost Spends"}
                            tooltipLabel="Aggregated spend grouped dynamically by Product, Vendor, or Publisher based on selected view."
                            data={totalCostSpendsChartData.data}
                            envNames={totalCostSpendsChartData.envNames}
                            seriesData={modifiedSeriesData}
                            isLoading={loadingState.totalCostSpend}
                            chartByCost={1}
                            axisMax={totalCostSpendsChartData.axisMax}
                            containerProps={{ sx: { height: '335px' } }}
                            hasDropdown={true}
                            dropdownValue={totalCostSpendsView}
                            dropdownOptions={[
                                { label: 'By Products', value: 'products' },
                                { label: 'By Vendors', value: 'vendors' },
                                { label: 'By Publishers', value: 'publishers' },
                            ]}
                            onDropdownChange={(value) => setTotalCostSpendsView(value as TotalCostSpendsView)}
                            variant="light"
                            maxVisibleBars={12}
                            chartMinHeight="380px"
                            chartTop="-16px"
                        />
                    ) : (
                        <LoaderComponent pt={16} />
                    )}
                </Grid>

                <Grid item xs={12} md={6}>
                    <PieChartWithLegendWidget
                        title="Cloud Vs On-Prem Ratio"
                        data={deploymentRatio.pieData}
                        colorMapping={getColorMapping(deploymentRatio.pieData)}
                        isLoading={loadingState.deploymentRatio}
                        hasTooltip={true}
                        tooltipText="Percentage distribution of active products deployed in Cloud versus On-Prem environments"
                        containerProps={{ sx: { minHeight: '318px' } }}
                    />
                </Grid>

                <Grid item xs={8} sm={12} md={6} lg={6} id="overview-table">
                    <Box>
                        {renderOverviewContent}
                    </Box>
                </Grid>
            </Grid>           
        </PageWrapper>
    );
};

export default ProductCatalogOverview;